import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff, Lock, LockOpen, Pencil, Trash2 } from 'lucide-react';
import CustomButton from '../../../components/CustomButton';
import {
    useArchiveCodeDoorMutation,
    useGetCodeDoorsQuery,
    useLazyRevealCodeDoorCodeQuery,
    useSetCodeDoorBlockedMutation,
} from '../../../api/accessCodeDoor/accessCodeDoorApiSlice';
import { CodeDoor } from '../../../api/accessCodeDoor/accessCodeDoorApiTypes';
import { enqueueErrorSnackbar } from '../../../action/snackbarCommands';
import CodeDoorFormDialog from './CodeDoorFormDialog';
import CodeDoorConfirmDialog from './CodeDoorConfirmDialog';

type ConfirmAction = 'block' | 'unblock' | 'remove';

interface AccessCodeSectionProps {
    assetId: string;
    /** Object name, for the create dialog's fixed-object line. */
    assetLabel: string;
    mode: 'view' | 'edit';
}

/**
 * The object's static access code — a master key lock's PIN, maintained here
 * and shown to the guest just-in-time inside the check-in/check-out flow.
 *
 * It lives on the OBJECT, not on the access-management page: the code opens
 * this one door, so it belongs with the object's other facts. Access
 * management stays the home of the IoT half, which is the paid add-on
 * (ADR-0001 Amendment 3 — superseding plan A5's placement).
 *
 * Secret discipline is unchanged (plan A2): the list payload never carries the
 * code, revealing it is a separate server-side event-logged fetch, and what is
 * revealed lives in component state until re-masked or unmount.
 *
 * The dialogs write immediately — they do not join the page's save cycle,
 * because a code write is its own endpoint with its own audit trail. The hint
 * line says so, so nobody expects the page's cancel to undo it.
 */
const AccessCodeSection = ({ assetId, assetLabel, mode }: AccessCodeSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { data: doors, isLoading, isError } = useGetCodeDoorsQuery();
    const [revealCode] = useLazyRevealCodeDoorCodeQuery();
    const [setBlocked, { isLoading: blocking }] = useSetCodeDoorBlockedMutation();
    const [archiveDoor, { isLoading: removing }] = useArchiveCodeDoorMutation();

    const [revealedCode, setRevealedCode] = useState<string | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

    const door = (doors ?? []).find((entry) => entry.assetId === assetId) ?? null;

    const toggleReveal = async (target: CodeDoor) => {
        if (revealedCode) {
            setRevealedCode(null);
            return;
        }
        try {
            const result = await revealCode({ id: target.id }).unwrap();
            setRevealedCode(result.code);
        } catch {
            dispatch(enqueueErrorSnackbar({ message: t('code_doors.reveal_error') }));
        }
    };

    const handleConfirm = async () => {
        if (!confirm || !door) return;
        try {
            if (confirm === 'remove') {
                await archiveDoor({ id: door.id }).unwrap();
                setRevealedCode(null);
            } else {
                await setBlocked({ id: door.id, blocked: confirm === 'block' }).unwrap();
            }
            setConfirm(null);
        } catch {
            dispatch(enqueueErrorSnackbar({ message: t(`code_doors.${confirm}_error`) }));
        }
    };

    const iconButtonClasses =
        'p-1.5 rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors';

    return (
        <section className={'bg-text-contrast rounded-[5px] shadow-sm px-5 py-4'}>
            <h2 className={'text-[15px] font-semibold text-foreground'}>
                {t('code_doors.object_title')}
            </h2>
            <p className={'mt-1 text-sm text-foreground/60'}>
                {t('code_doors.object_description')}
            </p>

            {isLoading && (
                <p className={'mt-4 text-sm text-foreground/60'}>{t('code_doors.loading')}</p>
            )}

            {/* A load failure must not read like "no code configured" — an
                operator who sees the empty state stops looking (#1579). */}
            {isError && !isLoading && (
                <p className={'mt-4 text-sm text-foreground'}>{t('code_doors.load_error')}</p>
            )}

            {!isLoading && !isError && !door && (
                <div className={'mt-4 flex flex-col gap-3'}>
                    <p className={'text-sm text-foreground/60'}>
                        {t('code_doors.object_empty')}
                    </p>
                    {mode === 'edit' && (
                        <div>
                            <CustomButton
                                title={t('code_doors.object_add_cta')}
                                onClick={() => setFormOpen(true)}
                            />
                        </div>
                    )}
                </div>
            )}

            {!isLoading && !isError && door && (
                <div className={'mt-4 flex flex-col gap-3'}>
                    <div className={'flex items-center justify-between gap-4'}>
                        <span className={'inline-flex items-center gap-2'}>
                            <span className={'font-mono text-foreground'}>
                                {revealedCode ?? '••••••'}
                            </span>
                            <button
                                type={'button'}
                                className={iconButtonClasses}
                                onClick={() => toggleReveal(door)}
                                aria-label={t(
                                    revealedCode ? 'code_doors.hide_code' : 'code_doors.reveal_code',
                                )}
                            >
                                {revealedCode
                                    ? <EyeOff className={'h-4 w-4'} />
                                    : <Eye className={'h-4 w-4'} />}
                            </button>
                        </span>
                        <span className={door.blocked
                            ? 'text-sm font-medium text-foreground'
                            : 'text-sm text-foreground/80'}
                        >
                            {t(door.blocked
                                ? 'code_doors.status_blocked'
                                : 'code_doors.status_active')}
                        </span>
                    </div>

                    {mode === 'edit' && (
                        <div className={'flex items-center gap-1'}>
                            <button
                                type={'button'}
                                className={iconButtonClasses}
                                onClick={() => setFormOpen(true)}
                                aria-label={t('code_doors.edit_cta')}
                            >
                                <Pencil className={'h-4 w-4'} />
                            </button>
                            <button
                                type={'button'}
                                className={iconButtonClasses}
                                onClick={() => setConfirm(door.blocked ? 'unblock' : 'block')}
                                aria-label={t(door.blocked
                                    ? 'code_doors.unblock_cta'
                                    : 'code_doors.block_cta')}
                            >
                                {door.blocked
                                    ? <LockOpen className={'h-4 w-4'} />
                                    : <Lock className={'h-4 w-4'} />}
                            </button>
                            <button
                                type={'button'}
                                className={iconButtonClasses}
                                onClick={() => setConfirm('remove')}
                                aria-label={t('code_doors.remove_cta')}
                            >
                                <Trash2 className={'h-4 w-4'} />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {mode === 'edit' && !isError && (
                <p className={'mt-3 text-xs text-foreground/60'}>
                    {t('code_doors.object_immediate_hint')}
                </p>
            )}

            {formOpen && (
                <CodeDoorFormDialog
                    door={door ?? undefined}
                    fixedAsset={door ? undefined : { id: assetId, label: assetLabel }}
                    onClose={() => setFormOpen(false)}
                />
            )}
            {confirm && door && (
                <CodeDoorConfirmDialog
                    door={door}
                    action={confirm}
                    loading={blocking || removing}
                    onConfirm={handleConfirm}
                    onClose={() => setConfirm(null)}
                />
            )}
        </section>
    );
};

export default AccessCodeSection;
